import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { NavigationService } from '../../shared/services/navigation.service';
import { ModalService } from '../../shared/services/modal.service';
import { Carrier } from '../../user-dashboard/shared/carrier.model';
import { WebhookSubscriptionService } from '../shared/webhook-subscription.service';
import {
  allEventTypes, allWebhookHttpVerbCallback,
  EventType,
  getDocumentationAnchorFor, WebhookHttpVerbCallback,
  WebhookSubscription,
  WebhookSubscriptionEvent
} from '../shared/webhook-subscription.model';
import { ErrorHandlerService } from '../../shared/services/error-handler.service';
import { LoaderService } from '../../shared/services/loader.service';
import { finalize } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { DocumentationAnchor, DocumentationTab } from '../webhook-documentation/webhook-documentation.component';

@Component({
  selector: 'app-webhook-subscriptions',
  templateUrl: './webhook-subscriptions.component.html',
  styleUrls: ['./webhook-subscriptions.component.scss']
})
export class WebhookSubscriptionsComponent implements OnChanges {

  @Input()
  private carrier: Carrier;

  @Output()
  public displayDocumentationEmitter = new EventEmitter<{ tab: DocumentationTab, anchor: DocumentationAnchor }>();

  public webhookSubscription: WebhookSubscription;

  public allWebhookHttpVerbs: WebhookHttpVerbCallback[]        = allWebhookHttpVerbCallback();
  public allEventTypes: EventType[]                            = allEventTypes();
  public allowedEventTypes: EventType[]                        = [];
  public newSubscriptionEvent: WebhookSubscriptionEvent = new WebhookSubscriptionEvent();
  public editSubscriptionEvent: WebhookSubscriptionEvent = new WebhookSubscriptionEvent();
  public editSubscriptionEventUrlCallback: string;
  public editSubscriptionEventUrlCallbackVerb: WebhookHttpVerbCallback;

  public formProcessingBlocked = false;

  @ViewChild('addEventForm')
  private addEventForm: NgForm;

  @ViewChild('editEventForm')
  private editEventForm: NgForm;

  @ViewChild('webhookSubscriptionForm')
  public webhookSubscriptionForm: NgForm;

  constructor(
    private navigation: NavigationService,
    private modal: ModalService,
    private translate: TranslateService,
    private webhookSubscriptionService: WebhookSubscriptionService,
    private errorHandler: ErrorHandlerService,
    private loader: LoaderService,
    private toastr: ToastrService,
  ) {
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.carrier && changes.carrier.currentValue) {
      this.webhookSubscriptionService.getSubscriptionBySubscriberId(this.carrier.id).subscribe(
        ws => {
          this.webhookSubscription = ws;
          setTimeout(() => this.webhookSubscriptionForm.form.markAsPristine(), 100);
        },
        error => this.errorHandler.handleHttpError(error, 'webhooks-events.subscriptions.errors.fetch-subscription-by-subscriber')
      );
    } else {
      this.webhookSubscription = undefined;
    }
  }

  public openAddEvent(content: TemplateRef<any>): void {
    this.newSubscriptionEvent = new WebhookSubscriptionEvent();
    const existingEventSubscriptions = this.webhookSubscription.subscriptionEvents.map(subEvent => subEvent.eventType);
    this.allowedEventTypes           = allEventTypes().filter(event => !existingEventSubscriptions.includes(event))

    this.modal.open({
      title: this.translate.instant('webhooks-events.subscriptions.events.modals.add-event.title'),
      content,
      validateButton: {
        text: this.translate.instant('buttons.add'),
        icon: 'fa fa-check',
        disabled: () => this.addEventForm && this.addEventForm.invalid,
      }
    }).subscribe(() => {
      this.webhookSubscription.subscriptionEvents.push(Object.assign({}, this.newSubscriptionEvent));
      // Note : ugly tricks due to modal, without timeout form doesn't take care of markAsDirty
      setTimeout(() => this.webhookSubscriptionForm.form.markAsDirty(), 100);
    });
  }

  public openEditEvent(content: TemplateRef<any>, subscriptionEvent: WebhookSubscriptionEvent): void {
    this.editSubscriptionEvent = subscriptionEvent;
    this.editSubscriptionEventUrlCallback = subscriptionEvent.urlCallback;
    this.editSubscriptionEventUrlCallbackVerb = subscriptionEvent.httpVerbCallback;

    this.modal.open({
      title: this.translate.instant('webhooks-events.subscriptions.events.modals.edit-event.title'),
      content,
      validateButton: {
        text: this.translate.instant('buttons.save'),
        icon: 'fa fa-save',
        disabled: () => this.editEventForm && this.editEventForm.invalid,
      }
    }).subscribe(() => {
      this.editSubscriptionEvent.urlCallback = this.editSubscriptionEventUrlCallback;
      this.editSubscriptionEvent.httpVerbCallback = this.editSubscriptionEventUrlCallbackVerb;
      this.editSubscriptionEventUrlCallback = null;
      this.editSubscriptionEventUrlCallbackVerb = null;
      // Note : ugly tricks due to modal, without timeout form doesn't take care of markAsDirty
      setTimeout(() => this.webhookSubscriptionForm.form.markAsDirty(), 100);
    });
  }

  public saveSubscription(): void {
    this.formProcessingBlocked = true;
    this.loader.startLoad();
    this.webhookSubscriptionService.saveSubscription(this.webhookSubscription)
      .pipe(finalize(() => {
        this.formProcessingBlocked = false;
        this.loader.stopLoad();
      }))
      .subscribe(webhookSubscription => {
        this.webhookSubscription = webhookSubscription;
        this.webhookSubscriptionForm.form.markAsPristine();
        this.toastr.success(this.translate.instant('webhooks-events.subscriptions.form.saved.success'));
      }, e => this.errorHandler.handleHttpError(e, 'webhooks-events.subscriptions.form.saved.error'));
  }

  public removeWebhookEventSubscription(webhookSubscriptionEvent: WebhookSubscriptionEvent): void {
    this.webhookSubscription.subscriptionEvents =
      this.webhookSubscription.subscriptionEvents.filter(se => webhookSubscriptionEvent.eventType !== se.eventType);
    this.webhookSubscriptionForm.form.markAsDirty();
  }

  public sendATry(webhookSubscriptionEvent: WebhookSubscriptionEvent): void {
    this.webhookSubscriptionService.testDryRun(webhookSubscriptionEvent).subscribe(dryRunResponse => {
        this.toastr.success(this.translate.instant(
          'webhooks-events.subscriptions.dry-run.success',
          { response: dryRunResponse.response ? dryRunResponse.response : '' }
        ));
      }, e => this.errorHandler.handleHttpError(e, 'webhooks-events.subscriptions.dry-run.error')
    );
  }

  public displayDocumentationFor(webhookSubscriptionEvent: WebhookSubscriptionEvent): void {
    this.displayDocumentationEmitter.emit({
      tab: DocumentationTab.TAB_EVENTS,
      anchor: getDocumentationAnchorFor(webhookSubscriptionEvent.eventType)
    });
  }
}
